// dependencies
import { format } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";

import { faExclamationCircle, faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

//components
import { AdminTitle } from "../../../../../components/Admin";
import Button from "../../../../../components/Button";
import TextInput from "../../../../../components/Inputs/InputText";
import Modal from "../../../../../components/Modal";
import ConfirmationModal from "../../../../../components/Modal/Templates/ConfirmationModal";
import OptionsDrawer from "../../../../../components/OptionsDrawer";
import PageTitle from "../../../../../components/PageTitle";
import Spacer from "../../../../../components/Spacer";
import TableLite from "../../../../../components/TableLite";
// types
import type { Column } from "../../../../../components/TableLite/types";
import Text from "../../../../../components/Text";
import TruncatedText from "../../../../../components/Text/TruncatedText";

// api
import { useCohorts } from "../../../../../api/hooks";
import useNavigate from "../../../../../hooks/useNavigate";
// hooks
import useToast from "../../../../../hooks/useToast";

import { deleteCohort } from "../../../../../api/services";

import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
// styles
import css from "./Cohorts.module.scss";
import { getSortedCohorts } from "./cohortsListUtils";

/**
 * Contains the table column definitions as well as the controls that exist in
 * the options drawer.
 */
const useTableColumnsAndOptionsControls = (
	maxCohortsReached = false,
	setMaxCohortReachedModal: (_value: boolean) => void
) => {
	const toast = useToast();
	const navigate = useNavigate();
	const { refetch } = useCohorts();
	const [deleteModal, setDeleteModal] = useState<null | { id: string; name: string }>(null);

	const handleDuplicate = async (cohortID: string) => {
		if (maxCohortsReached) {
			setMaxCohortReachedModal(true);
		} else {
			try {
				navigate(`/administration/engagement/duplicate-cohort/${cohortID}`);
			} catch (error) {
				toast((error as Error).message, {
					type: "error",
					size: "large",
				});
			}
		}
	};

	const handleDelete = async () => {
		if (!deleteModal) return;
		await deleteCohort(deleteModal.id);
		refetch();
		setDeleteModal(null);
	};

	const handleCancelModal = () => {
		setDeleteModal(null);
	};

	const modal = deleteModal ? (
		<ConfirmationModal
			positiveAction={handleDelete}
			negativeAction={handleCancelModal}
			positiveActionLabel="Yes"
			negativeActionLabel="Cancel"
		>
			<>
				Are you sure you want to delete the cohort &quot;
				<Text element="span" size="paragraph-2" weight="semi-bold" className={css.deleteModal}>
					{deleteModal.name}
				</Text>
				&quot;?
			</>
		</ConfirmationModal>
	) : null;

	const tableCols = useMemo(() => {
		return [
			{
				id: "name",
				label: "Cohort",
				width: "2fr",
				getter: (row) => {
					return (
						<div className={css.nameCell}>
							<div className={css.cohortColor} style={{ backgroundColor: row.color }} />
							<TruncatedText
								text={row.name}
								maxWidth={250}
								size="paragraph-3"
								weight="regular"
								className={css.cohortName}
							/>
						</div>
					);
				},
			},
			{
				id: "cohortType",
				label: "Cohort Type",
				getter: (row) => {
					return row.type === "static" ? "Static" : "Dynamic";
				},
			},
			{
				id: "createdAt",
				label: "Date Created",
				getter: (row) => {
					return format(row.createdAt, "MMM dd, yyyy");
				},
			},
			{
				id: "updatedAt",
				label: "Last Modified",
				getter: (row) => {
					return format(row.updatedAt, "MMM dd, yyyy");
				},
			},
			{
				id: "memberCount",
				label: "Cohort Size",
				getter: (row) => {
					if (row.memberCount < 3) {
						return (
							<span className={css.cohortSizeCell}>
								{row.memberCount}
								<span
									className={css.sizeWarningIcon}
									tooltime="200"
									position="right"
									title={
										"Analytics dashboard charts will only display results for a cohort if a sufficient number of cohort members (3) have responded to surveys."
									}
								>
									<FontAwesomeIcon
										icon={faExclamationCircle}
										color={"red"}
										data-testid="circle-exclamation"
									/>
								</span>
							</span>
						);
					}
					return row.memberCount;
				},
			},
			{
				id: "status",
				label: "Status",
				getter: (row) => {
					const labels = {
						idle: {
							label: "Active",
							color: "green",
						},
						backpropagating: {
							label: "Processing",
							color: "grey",
						},
						processing: {
							label: "Processing",
							color: "grey",
						},
					};

					const config = labels[row.calculation_status] ?? { label: "Active", color: "green" };

					return (
						<div className={`${css.tableChip} ${css[config.color]}`}>
							<Text size="paragraph-3" weight="semi-bold">
								{config.label}
							</Text>
						</div>
					);
				},
			},
			{
				id: "options",
				label: "",
				isSortable: false,
				width: "32px",
				getter: (row) => {
					const handleSelect = async (option: { id: string; label: string }) => {
						switch (option.id) {
							case "edit":
								navigate(`/administration/engagement/cohorts/${row._id}`);
								break;
							case "duplicate": {
								handleDuplicate(row._id);
								break;
							}
							case "delete":
								setDeleteModal({ id: row._id, name: row.name });
								break;
						}
					};

					return (
						<div className={css.tableOptions}>
							<OptionsDrawer
								openOn="bottom"
								horizontal={true}
								onSelect={handleSelect}
								options={[
									{ id: "edit", label: "Edit" },
									{ id: "duplicate", label: "Duplicate" },
									{ id: "delete", label: "Delete" },
								]}
							/>
						</div>
					);
				},
			},
		] satisfies Column[];
	}, [navigate]);

	return {
		modal,
		columns: tableCols,
	};
};

const CohortTab = () => {
	// state management
	const [search, setSearch] = useState<string>("");
	const [sort, setSort] = useState<{ column: string; direction: "asc" | "desc" }>({
		column: "name",
		direction: "asc",
	});
	const [maxCohortsReachedModalOpen, setMaxCohortsReachedModalOpen] = useState<boolean>(false);

	// handle row and column data
	const { data, isLoading, refetch } = useCohorts();
	const allCohorts = data?.cohorts ?? [];
	const maximumAllowedCohorts = data?.maximumAllowedCohorts ?? 0;
	const currentCohortCount = data?.currentCohortCount ?? 0;
	const maxCohortsReached = currentCohortCount >= maximumAllowedCohorts;

	const { columns, modal: deleteModal } = useTableColumnsAndOptionsControls(maxCohortsReached, (value) =>
		setMaxCohortsReachedModalOpen(value)
	);

	const pendingCohorts = allCohorts.filter((cohort) => cohort.calculation_status !== "idle").length;

	useEffect(() => {
		let refetchTimeout;
		if (pendingCohorts > 0) {
			refetchTimeout = setInterval(() => {
				refetch();
			}, 5000);
		}
		return () => {
			clearInterval(refetchTimeout);
		};
	}, [pendingCohorts]);

	// search and sort cohorts
	const cohorts = useMemo(() => {
		const items = allCohorts;

		return getSortedCohorts(items, sort.column, sort.direction, search);
	}, [data, search, sort]);

	const handleSort = (column: string, direction: "asc" | "desc") => {
		setSort({ column, direction });
	};

	return (
		<>
			<PageTitle title="Employee Engagement Settings" />
			<AdminTitle
				description="Customize and adjust settings to only display information that is relevant to you and your team."
				includeSpacer={false}
			>
				Employee Cohorts
			</AdminTitle>

			{deleteModal}
			<Spacer height={2} />

			<div className={css.tableControls}>
				<TextInput
					value={search}
					onChange={setSearch}
					placeholder="Search fields..."
					icon={faSearch}
					clearable
				/>

				<div />

				<div>
					<Button
						href={!maxCohortsReached ? "/administration/engagement/create-cohort" : undefined}
						onClick={maxCohortsReached ? () => setMaxCohortsReachedModalOpen(true) : undefined}
						className={css.createCohortButton}
						color="blue"
						icon={faPlus}
						iconPosition="left"
					>
						Create Cohort
					</Button>
				</div>
			</div>

			<Spacer height={2} />

			<div className={css.main}>
				<TableLite
					theme="vJan23"
					activeSort={sort.column}
					activeSortDirection={sort.direction}
					rowKey="_id"
					columns={columns}
					rows={cohorts}
					onColumnClick={handleSort}
					isLoading={isLoading}
				/>

				{!isLoading && cohorts.length === 0 ? (
					<div className={css.emptyState}>
						{allCohorts.length === 0 ? (
							<div>
								<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
									You currently have no Employee Cohorts.
								</Text>
								<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
									Create a Cohort to begin.
								</Text>

								<Spacer height={2} />

								<Button
									href="/administration/engagement/create-cohort"
									className={css.createCohortButton}
									color="blue"
									icon={faPlus}
									iconPosition="left"
								>
									Create Cohort
								</Button>
							</div>
						) : (
							<EmptySearchTableMessage />
						)}
					</div>
				) : null}
			</div>

			{maxCohortsReachedModalOpen && (
				<Modal
					content={
						<Text weight="semi-bold" size="paragraph-1" className={css.maxCohortsModal}>
							You have reached the maximum {maximumAllowedCohorts} Cohort limit. Consider removing old
							cohorts to free up more space.
						</Text>
					}
					buttons={
						<Button color="green" onClick={() => setMaxCohortsReachedModalOpen(false)}>
							Close
						</Button>
					}
					handleClose={() => setMaxCohortsReachedModalOpen(false)}
					size="large"
				/>
			)}
		</>
	);
};

export default CohortTab;
